import styled from "@emotion/styled";
import dayjs from "dayjs";

import { Button, Skeleton } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { navigateToBillingPortal, navigateToPaymentMethod } from "#actions";

import {
  useAccount,
  useCurrentPlanName,
  useHasStripeAccount,
  useInteractProduct,
  useRefreshAccess,
} from "../useAccount";
import { FREEDOM_DATE_FORMAT } from "../utils";
import { Card } from "./Card";
import { cardGap } from "./utils";

// hack, we need to consistently render prices on server or client
const formatUsd = (value: number | null | undefined) => {
  if (value == null) return "-"; // Handle null or undefined values
  return `$${(value / 100).toFixed(2)}`;
};

export const BillingOverviewCards = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const account = useAccount()?.data;
  const product = useInteractProduct();
  const refreshAccount = useRefreshAccess();
  const hasStripeAccount = useHasStripeAccount();
  const currentPlanName = useCurrentPlanName();
  const sub = product?.subscription;

  return (
    <CardListContainer>
      <Card header={formatMessage("current")}>
        <CardValue>{formatUsd(account?.stripe?.balance)}</CardValue>
        <Button disabled={!hasStripeAccount} color="primary" onClick={navigateToBillingPortal} variant="outlined">
          {formatMessage("manageBilling")}
        </Button>
      </Card>

      <Card header={formatMessage("next")}>
        {account ? (
          <>
            {account.stripe?.nextInvoice
              ? dayjs(account.stripe.nextInvoice.dueDate * 1000).format(FREEDOM_DATE_FORMAT)
              : "-"}
            <CardValue>{formatUsd(account.stripe?.nextInvoice?.total)}</CardValue>
          </>
        ) : (
          <Skeleton variant="rounded" height={30} width="100%" animation="wave" />
        )}
      </Card>

      <Card header={formatMessage("currentSubscription")}>
        {!product ? (
          <Skeleton variant="rounded" height={30} width="100%" animation="wave" />
        ) : !sub?.pendingUpdate ? (
          currentPlanName
        ) : (
          <>
            {"Pending..."}
            <Button variant="outlined" onClick={refreshAccount}>
              Refresh
            </Button>
          </>
        )}
      </Card>

      <Card header={formatMessage("payment")}>
        <Button disabled={!hasStripeAccount} onClick={navigateToPaymentMethod} variant="outlined">
          {formatMessage("updatePayment")}
        </Button>
      </Card>
    </CardListContainer>
  );
};

const CardListContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${cardGap}px;
`;

const CardValue = styled.div`
  font-size: 24px;
`;

const formatStrings = {
  current: {
    en: "Current Monthly Bill",
  },
  currentSubscription: {
    en: "Current Plan",
  },
  next: {
    en: "Next Payment Due",
  },
  payment: {
    en: "Payment Information",
  },
  updatePayment: {
    en: "Update Payment Method",
  },
  manageBilling: {
    en: "Manage Billing",
  },
};
